import type {
  AccountsReceivableFilterOptionsResponse,
  AccountsReceivableQueryParams,
  AccountsReceivableResponse,
  AccountsReceivableInvoice,
} from '@/types/accountsReceivable.types';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';
import type { GenericErrorResponse } from '@brainvaulttech/dgr-react-sdk';

export const getAccountsReceivableData = async (
  params: AccountsReceivableQueryParams
): Promise<AccountsReceivableResponse> => {
  const response = await axios.get<AccountsReceivableResponse>('/accounts/receivable', {
    params: {
      ...params,
      clientIds: params.clientIds?.length ? JSON.stringify(params.clientIds) : undefined,
      invoiceNumbers: params.invoiceNumbers?.length ? JSON.stringify(params.invoiceNumbers) : undefined,
      fileNumbers: params.fileNumbers?.length ? JSON.stringify(params.fileNumbers) : undefined,
    },
  });
  return response.data;
};

export const useAccountsReceivable = (params: AccountsReceivableQueryParams, enabled = true) => {
  return useQuery<AccountsReceivableResponse>({
    queryKey: ['accounts-receivable', params],
    queryFn: () => getAccountsReceivableData(params),
    staleTime: 1000 * 60 * 5,
    enabled,
  });
};

export const useAccountsReceivableFilterOptions = (enabled = true) => {
  return useQuery<AccountsReceivableFilterOptionsResponse>({
    queryKey: ['accounts-receivable-filter-options'],
    queryFn: () =>
      axios.get<AccountsReceivableFilterOptionsResponse>('/accounts/receivable/filter-options').then((res) => res.data),
    staleTime: 1000 * 60 * 5,
    enabled,
  });
};

export const getAccountsReceivable = (
  params: AccountsReceivableQueryParams,
  options?: { query?: Partial<UseQueryOptions<AccountsReceivableResponse>> }
): {
  data: AccountsReceivableInvoice[];
  totalRows: number;
  totalAmountDue?: string;
  lessThan30DaysDue?: string;
  past30DaysDue?: string;
  past60DaysDue?: string;
  past90DaysDue?: string;
  invoiceNumbers?: string[];
  isFetching: boolean;
} => {
  const query = useQuery<AccountsReceivableResponse>({
    queryFn: () => getAccountsReceivableData(params),
    queryKey: ['accounts-receivable', params],
    ...options?.query,
  });

  const responseData = query.data;
  const data = responseData?.data ?? [];
  const invoiceNumbers = responseData?.invoiceNumbers ?? [];
  const totalRows = responseData?.totalRows ?? 0;
  const totalAmountDue = responseData?.totalAmountDue;
  const lessThan30DaysDue = responseData?.lessThan30DaysDue;
  const past30DaysDue = responseData?.past30DaysDue;
  const past60DaysDue = responseData?.past60DaysDue;
  const past90DaysDue = responseData?.past90DaysDue;
  return {
    data,
    totalRows,
    totalAmountDue,
    lessThan30DaysDue,
    invoiceNumbers,
    past30DaysDue,
    past60DaysDue,
    past90DaysDue,
    isFetching: query.isFetching,
  };
};

export const useMarkAsFailedTransaction = () => {
  return useMutation<
    { message: string },
    AxiosError<GenericErrorResponse>,
    { invoiceNumbers: string[]; isFailedTransaction: boolean }
  >({
    mutationFn: async ({ invoiceNumbers, isFailedTransaction }) => {
      const response = await axios.patch<{ message: string }>('/accounts/receivable/mark-failed', {
        invoiceNumbers,
        isFailedTransaction,
      });
      return response.data;
    },
  });
};
